import { useCallback, useMemo } from 'react';

import type { FamilyActivityItem } from '@/features/guardian/components/family-activity-section';
import { toFamilyActivityItem } from '@/features/guardian/components/family-activity-section/utils';
import type { CoGuardianProfileVM } from '@/features/guardian-player/types';
import { buildCoGuardianProfile } from '@/features/guardian-player/utils';
import { useCoGuardianActivitiesQuery } from '@/queries/use-co-guardian-activities-query';
import { useCoGuardianQuery } from '@/queries/use-co-guardian-query';

export type UseCoGuardianProfileResult = {
  profile?: CoGuardianProfileVM;
  activities: FamilyActivityItem[];
  isLoading: boolean;
  isActivitiesLoading: boolean;
  isError: boolean;
  /** True while either request is refetching (e.g. pull-to-refresh) after the initial load. */
  isRefetching: boolean;
  /** Refetches both the profile and its activities, resolving once both have settled. */
  refetch: () => Promise<void>;
};

/**
 * View model for the Co-Guardian Profile screen: the member's identity + mutual kids
 * (from `/guardians/me/players/{playerId}/guardians/{guardianUid}`) and their player-scoped
 * recent activities (from the same path + `/activities`, BR-01), reusing the family-activity
 * row mapping.
 */
export function useCoGuardianProfile(
  playerId?: string,
  guardianUid?: string,
): UseCoGuardianProfileResult {
  const {
    data: profileData,
    isLoading,
    isError,
    isRefetching: isProfileRefetching,
    refetch,
  } = useCoGuardianQuery(playerId, guardianUid);

  const {
    data: activitiesData,
    isLoading: isActivitiesLoading,
    isRefetching: isActivitiesRefetching,
    refetch: refetchActivities,
  } = useCoGuardianActivitiesQuery(playerId, guardianUid);

  const profile = useMemo(() => buildCoGuardianProfile(profileData), [profileData]);

  const activities = useMemo(
    () => (activitiesData?.activities ?? []).map(toFamilyActivityItem),
    [activitiesData],
  );

  const refetchAll = useCallback(async () => {
    await Promise.all([refetch(), refetchActivities()]);
  }, [refetch, refetchActivities]);

  return {
    profile,
    activities,
    isLoading,
    isActivitiesLoading,
    isError,
    isRefetching: isProfileRefetching || isActivitiesRefetching,
    refetch: refetchAll,
  };
}
